import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Phone, X } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { CLINIC } from "@/data/services";
import Logo from "@/components/Logo";
import { trackAgendarCitaClick } from "@/analytics";

const LINKS = [
  { id: "servicios", label: "Servicios" },
  { id: "nosotros", label: "Nosotros" },
  { id: "laboratorio", label: "Laboratorio" },
  { id: "contacto", label: "Contacto" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  const agendar = (location) => {
    trackAgendarCitaClick(location);
    go("contacto");
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      data-testid="main-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
        scrolled
          ? "border-b border-slate-200/80 bg-white/80 shadow-sm backdrop-blur-xl"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6">
        <button
          data-testid="navbar-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3 text-left"
        >
          <Logo className="h-11" />
        </button>

        <nav className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="group relative text-sm font-medium text-slate-600 transition-colors duration-300 hover:text-slate-900"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-red-600 transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          <a
            data-testid="navbar-phone-badge"
            href={`tel:${CLINIC.phoneIntl}`}
            className="flex items-center gap-2 font-mono text-xs tracking-wider text-slate-600 transition-colors hover:text-red-700"
          >
            <Phone size={14} className="text-red-600" />
            {CLINIC.phone}
          </a>
          <button
            data-testid="navbar-agendar-cta"
            onClick={() => agendar("navbar")}
            className="rounded-full bg-black px-5 py-2.5 text-sm font-medium text-white transition-[background-color,transform] duration-300 hover:bg-red-700 hover:scale-[1.03] active:scale-95"
          >
            Agendar Cita
          </button>
        </div>

        <button
          data-testid="navbar-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="text-slate-900 lg:hidden"
          aria-label="Abrir menú"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            data-testid="navbar-mobile-menu"
            className="overflow-hidden border-b border-slate-200 bg-white/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => go(l.id)}
                  className="rounded-lg px-3 py-3 text-left text-base font-medium text-slate-700 hover:bg-slate-100"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-mobile-agendar-cta"
                onClick={() => agendar("navbar_movil")}
                className="mt-2 rounded-full bg-black px-5 py-3 text-sm font-medium text-white"
              >
                Agendar Cita · {CLINIC.phone}
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
